import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingBag, Shield, Eye, Zap, CheckCircle, Users, 
  TrendingUp, Award, Heart, Star, MapPin, Phone, Mail,
  ArrowRight, Sparkles, Target, Home, ArrowLeft, Info,
  Globe, Rocket, BookOpen, MessagesSquare
} from 'lucide-react';

export default function AboutPage() {
  const navigate = useNavigate();

  // 📊 Données des fonctionnalités
  const features = [
    {
      icon: Shield,
      color: 'from-blue-500 to-cyan-500',
      title: 'Sécurité',
      description: 'Les vendeurs sont identifiés et les transactions sont encadrées pour limiter les abus et renforcer la confiance.',
      details: 'Système de vérification des comptes, modération active, signalement rapide des fraudes'
    },
    {
      icon: Eye,
      color: 'from-orange-500 to-yellow-500',
      title: 'Transparence',
      description: 'Pas de frais cachés. Les informations sont claires, les prix visibles et le contact direct entre acheteur et vendeur.',
      details: 'Aucun frais caché, prix affichés clairement, communication directe sans intermédiaire'
    },
    {
      icon: Zap,
      color: 'from-green-500 to-emerald-500',
      title: 'Rapidité',
      description: 'Publiez une annonce en quelques minutes et trouvez rapidement des acheteurs intéressés.',
      details: 'Publication instantanée, notifications en temps réel, recherche optimisée'
    }
  ];

  // 📈 Statistiques
  const stats = [
    {
      icon: CheckCircle,
      gradient: 'from-blue-500 to-cyan-500',
      number: '100%',
      label: 'Annonces vérifiées',
      description: 'Chaque annonce est contrôlée'
    },
    {
      icon: Users,
      gradient: 'from-orange-500 to-yellow-500',
      number: '24/7',
      label: 'Assistance disponible',
      description: 'Support toujours accessible'
    },
    {
      icon: Award,
      gradient: 'from-green-500 to-emerald-500',
      number: '0',
      label: 'Intermédiaire imposé',
      description: 'Contact direct garanti'
    }
  ];

  // 🎯 Nos engagements
  const engagements = [
    {
      icon: Star,
      gradient: 'from-yellow-500 to-orange-500',
      title: 'Qualité des annonces',
      description: 'Annonces détaillées avec descriptions claires pour aider les acheteurs à prendre les bonnes décisions.'
    },
    {
      icon: Users,
      gradient: 'from-purple-500 to-pink-500',
      title: 'Assistance disponible',
      description: 'Une équipe dédiée pour accompagner et améliorer votre expérience sur la plateforme.'
    },
    {
      icon: Heart,
      gradient: 'from-red-500 to-pink-500',
      title: 'Liberté de négociation',
      description: 'Aucun intermédiaire imposé : négociez librement vos transactions en toute autonomie.'
    }
  ];

  // 🌍 Notre vision
  const visionPoints = [
    {
      icon: Globe,
      title: 'Local & Accessible',
      description: 'Une plateforme conçue pour la réalité ivoirienne et accessible à tous'
    },
    {
      icon: Rocket,
      title: 'Innovation Continue',
      description: 'Des fonctionnalités en constante évolution pour mieux vous servir'
    },
    {
      icon: Users,
      title: 'Communauté Active',
      description: 'Un réseau d\'acheteurs et vendeurs en croissance constante'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-20 w-96 h-96 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 -right-20 w-96 h-96 bg-gradient-to-tl from-purple-500/15 to-blue-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '500ms' }}></div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-white/5 border-b border-white/10 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate(-1)}
                className="group p-2.5 text-white/70 hover:text-purple-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <ArrowLeft size={20} className="relative z-10" />
              </button>
              <div 
                className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent cursor-pointer hover:scale-105 transition-transform"
                onClick={() => navigate('/')}
              >
                MonMarket
              </div>
              <span className="hidden sm:block text-white/20">|</span>
              <span className="hidden sm:flex items-center space-x-2 text-white font-bold">
                <Info className="text-orange-400" size={20} />
                <span>À Propos</span>
              </span>
            </div>
            <button 
              onClick={() => navigate('/')}
              className="group p-2.5 text-white/70 hover:text-orange-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <Home size={20} className="relative z-10" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 relative z-10">
        {/* Hero Section */}
        <div className="mb-8 text-center">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-pink-500 rounded-full blur-2xl opacity-60 animate-pulse"></div>
            <div className="relative w-20 h-20 bg-gradient-to-br from-orange-500 to-pink-500 rounded-full flex items-center justify-center backdrop-blur-xl border-4 border-white/20 shadow-2xl mx-auto transform hover:rotate-12 transition-transform">
              <ShoppingBag size={40} className="text-white" />
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4">
            MonMarket CI
          </h1>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto mb-2">
            La plateforme de mise en relation directe entre acheteurs et vendeurs
          </p>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Conçue pour la Côte d'Ivoire, par des Ivoiriens
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {stats.map((stat, idx) => (
            <div key={idx} className="group relative">
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.gradient} rounded-2xl blur opacity-0 group-hover:opacity-5 transition duration-500`}></div>
              <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all transform hover:scale-105">
                <div className={`w-12 h-12 bg-gradient-to-br ${stat.gradient} rounded-xl flex items-center justify-center mb-4 shadow-lg transform group-hover:rotate-12 transition-transform`}>
                  <stat.icon className="text-white" size={24} />
                </div>
                <div className="text-3xl font-black text-white mb-1">{stat.number}</div>
                <div className="text-sm text-gray-400 font-medium mb-2">{stat.label}</div>
                <div className="text-xs text-gray-500">{stat.description}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Notre Histoire */}
        <div className="relative group mb-8">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-3xl blur opacity-0 group-hover:opacity-5 transition duration-500"></div>
          <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl p-8 border border-white/20">
            <div className="flex items-start space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-xl">
                <Sparkles size={32} className="text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-white mb-3">Notre Histoire</h2>
                <p className="text-gray-400">Comment MonMarket est né d'un besoin réel</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="text-red-400" size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">Un problème réel en Côte d'Ivoire</h3>
                    <p className="text-gray-300 leading-relaxed">
                      En Côte d'Ivoire, le commerce en ligne est en pleine croissance, mais reste confronté à plusieurs défis : 
                      <strong className="text-white"> annonces peu fiables, vendeurs anonymes, arnaques fréquentes</strong> et 
                      manque de plateformes locales adaptées.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="text-green-400" size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">Notre Solution</h3>
                    <p className="text-gray-300 leading-relaxed mb-4">
                      MonMarket est né pour répondre à ces problématiques en proposant un 
                      <strong className="text-white"> marketplace ivoirien de mise en relation directe</strong>, 
                      pensé pour la réalité locale.
                    </p>
                    <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 flex items-start space-x-3">
                      <Target className="text-orange-400 flex-shrink-0 mt-0.5" size={20} />
                      <div>
                        <p className="text-white font-bold mb-2">🎯 Notre Mission</p>
                        <p className="text-gray-300 text-sm">
                          Faciliter la vente et l'achat de produits en ligne en Côte d'Ivoire en offrant une plateforme 
                          simple, transparente, sans intermédiaires imposés et accessible à tous.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Nos Piliers */}
        <div className="mb-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl mb-4 shadow-xl">
              <Award size={28} className="text-white" />
            </div>
            <h2 className="text-3xl font-black text-white mb-2">Nos Piliers</h2>
            <p className="text-gray-400">Les valeurs qui guident chacune de nos actions</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="group relative"
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${feature.color} rounded-2xl blur opacity-0 group-hover:opacity-5 transition duration-500`}></div>
                <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all transform hover:scale-105 h-full">
                  <div className={`w-14 h-14 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-4 transform group-hover:rotate-12 transition-transform shadow-lg`}>
                    <feature.icon size={28} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed mb-3">
                    {feature.description}
                  </p>
                  <div className="text-xs text-gray-500 border-t border-white/10 pt-3">
                    💡 {feature.details}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Nos Engagements */}
        <div className="mb-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl mb-4 shadow-xl">
              <CheckCircle size={28} className="text-white" />
            </div>
            <h2 className="text-3xl font-black text-white mb-2">Nos Engagements</h2>
            <p className="text-gray-400">Des garanties concrètes pour votre tranquillité</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {engagements.map((item, idx) => (
              <div
                key={idx}
                className="group relative"
              >
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${item.gradient} rounded-2xl blur opacity-0 group-hover:opacity-5 transition duration-500`}></div>
                <div className="relative bg-white/10 backdrop-blur-2xl rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all">
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                      <item.icon className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-orange-400 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notre Vision */}
        <div className="mb-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl mb-4 shadow-xl">
              <Rocket size={28} className="text-white" />
            </div>
            <h2 className="text-3xl font-black text-white mb-2">Notre Vision</h2>
            <p className="text-gray-400">Devenir la référence du e-commerce en Côte d'Ivoire</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {visionPoints.map((point, idx) => (
              <div
                key={idx}
                className="bg-white/10 backdrop-blur-2xl rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all"
              >
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <point.icon className="text-orange-400" size={20} />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white mb-2">{point.title}</h4>
                    <p className="text-gray-300 text-sm">{point.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Vision CTA */}
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 rounded-3xl blur opacity-20 group-hover:opacity-5 transition duration-500"></div>
            <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl p-8 border border-orange-500/30 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-pink-500 rounded-2xl mb-6 shadow-2xl transform hover:rotate-12 transition-transform">
                <MapPin size={32} className="text-white" />
              </div>
              
              <h3 className="text-2xl sm:text-3xl font-black text-white mb-4">
                MonMarket : Le Marketplace ivoirien pour tous
              </h3>
              
              <p className="text-gray-300 leading-relaxed mb-4 max-w-3xl mx-auto">
                Nous nous adressons aux <strong className="text-white">particuliers, commerçants, entrepreneurs et vendeurs indépendants</strong>. 
                Que vous souhaitiez vendre un produit, acheter en ligne en Côte d'Ivoire, ou développer votre activité, 
                MonMarket vous offre une solution adaptée et locale.
              </p>
              
              <p className="text-gray-300 leading-relaxed mb-6 max-w-3xl mx-auto">
                Notre ambition est de devenir <strong className="text-white">la référence du marketplace en Côte d'Ivoire</strong>, 
                en mettant la confiance, la simplicité et l'accessibilité au cœur du commerce digital.
              </p>

              <button
                onClick={() => navigate('/')}
                className="group/btn inline-flex items-center space-x-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-8 py-4 rounded-2xl font-bold shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all"
              >
                <ShoppingBag size={24} />
                <span>Découvrir nos produits</span>
                <ArrowRight size={20} className="group-hover/btn:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-3xl blur opacity-0 group-hover:opacity-5 transition duration-500"></div>
          <div className="relative bg-white/10 backdrop-blur-2xl rounded-3xl p-8 border border-white/20">
            <h2 className="text-2xl font-black text-white mb-6 flex items-center space-x-3">
              <MessagesSquare className="text-purple-400" size={28} />
              <span>📬 Contactez-nous</span>
            </h2>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-green-500/30 transition-all">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                    <Phone className="text-white" size={20} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-1">Téléphone</div>
                    <div className="text-white font-bold text-sm">+225 01 42 50 77 50</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-blue-500/30 transition-all">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                    <Mail className="text-white" size={20} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-1">Email</div>
                    <div className="text-white font-bold text-sm">contact@monmarket.ci</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-orange-500/30 transition-all">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center">
                    <MapPin className="text-white" size={20} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-1">Localisation</div>
                    <div className="text-white font-bold text-sm">Abidjan, Côte d'Ivoire</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}